import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { logFeature } from '@office-start/telemetry-service';
import { KnownFeatureInstrumentationEvents, KnownFeatureNames, KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';
import { filter, take } from 'rxjs/operators';

import { ApplicationError, OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { OfficeRoute } from '../constants/office-route.enum';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp } from '../instrumentation/instrumented-http.service';
import { HttpRequestOptions } from '../models/http-request-options.model';
import { AngularEvents } from '../shared-context/angular-events';
import { ContextService } from '../shared-context/context.service';
import { AccessTokenService } from './access-token.service';
import { AuthStateConfigService } from './auth-state-config.service';
import { EventService } from './event.service';
import { OfficeRouteService } from './office-route.service';
import { OfficeHomeApiConfigService } from './officehome-api-config.service';
import { PageVisibilityService } from './page-visibility.service';
import { WindowService } from './window.service';

export const enum AuthState {
    SignedIn = 'SignedIn',
    SignedOut = 'SignedOut',
    SignedOutBasedOnActivityAuto = 'SignedOutBasedOnActivityAuto', // Event where user is signed out automatically due to inactivity
    SignedOutBasedOnActivityUserClick = 'SignedOutBasedOnActivityUserClick', // User saw ABT signout dialog and clicked the Sign Out button to manually trigger signout.
    Expired = 'Expired',
    ReauthenticationRequired = 'ReauthenticationRequired',
    PolicyCheckFailed = 'PolicyCheckFailed',
    AccountMismatch = 'AccountMismatch'
}

export interface CheckLoginResponse {
    LoginState: AuthState;
    CurrentAccountPuid?: string;
    CurrentAccountUpn?: string;
    CurrentAccountType?: string;
}

interface AbtSignoutEventData {
    eventId: string;
    detail: AbtSignoutEventDataDetail;
}

interface AbtSignoutEventDataDetail {
    userId: string;
    signoutPageUrl: string;
}

@Injectable({
    providedIn: 'root'
})
export class AuthStateService {

    private currentState = AuthState.SignedIn;
    private initialized = false;
    private checkAuthStateEndpoint: string;
    private checkAuthStateRequestOptions: HttpRequestOptions;
    private queryParamProcessor: Promise<void>;
    private checkAuthStateTimeout: number = null;
    private accessTokenService: AccessTokenService = null;

    constructor(
        private readonly contextService: ContextService,
        private readonly configService: AuthStateConfigService,
        private readonly officeHomeApiConfigService: OfficeHomeApiConfigService,
        private readonly instrumentationService: InstrumentationService,
        private readonly instrumentedHttp: InstrumentedHttp,
        private readonly eventService: EventService,
        private readonly pageVisibilityService: PageVisibilityService,
        private readonly route: ActivatedRoute,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly windowService: WindowService,
        private readonly routeService: OfficeRouteService
    ) {
    }

    public initialize(accessTokenService: AccessTokenService) {
        if (this.initialized) {
            throw new ApplicationError('Not allowed to call AuthStateService.initialize() more than once');
        }

        this.initialized = true;
        this.accessTokenService = accessTokenService;

        const authStateConfig = this.configService.config;
        if (!(authStateConfig.checkAuthStateEnabled && authStateConfig.checkAuthStateEndpoint)) {
            return;
        }

        const sharedContext = this.contextService.sharedContext;
        this.checkAuthStateEndpoint = `${sharedContext.officeHomeApiRoot}/${authStateConfig.checkAuthStateEndpoint}`;
        this.checkAuthStateRequestOptions = { headers: new Map() };

        this.queryParamProcessor = new Promise<void>((resolve, reject) => {
            if (!sharedContext.inMockMode) {
                resolve();
            }

            if (!this.officeHomeApiConfigService.config.mockRequestHeader) {
                resolve();
            }

            this.route.queryParams.pipe(
                filter(queryParams => Object.keys(queryParams).length > 0),
                take(1))
                .toPromise()
                .then(queryParams => {
                    for (const param in queryParams) {
                        if (param && queryParams.hasOwnProperty(param) && param.toLowerCase() === 'mockchecklogin') {
                            this.checkAuthStateRequestOptions.headers.set(this.officeHomeApiConfigService.config.mockRequestHeader, queryParams[param]);
                        }
                    }
                    resolve();
                })
                .catch(error => {
                    // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                    this.globalErrorHandler.handleError(error, ErrorId.Jrzf6);

                    reject(error);
                });
        });

        // Track auth ticket expiration
        this.watchAuthTicketExpiry();

        // Determine time when reauthentication is required in case of permanent error with access tokens
        this.watchAccessTokenPermanentError();

        // Track changes in page visibility and check auth state when page becomes visible after long time being in background
        this.watchPageVisibilityChanges();

        // Track auth state by pinging checkLogin endpoint on periodic basis
        this.scheduleCheckAuthState();

        // Setup message listener for Activity-based Timeout signout events
        this.setupActivityTimeoutSignoutListener();
    }

    public getCurrentState(): AuthState {
        return this.currentState;
    }

    private watchAuthTicketExpiry(): void {
        // Kick off timer that expires with auth ticket
        if (this.configService.config.authTicketExpiryIntervalInMinutes > 0) {
            let expiryTimeout: number;
            const timeoutInterval = this.configService.config.authTicketExpiryIntervalInMinutes * 60000;
            const pageLoadStart = Date.now();

            // This is hopefully a temporary workaround to prevent expiring the auth ticket if on a UI host
            // route. The timeout will still fire after the expiry interval if the user navigates away from
            // a content route.
            // Longer-term we should flight a change to remove this timeout entirely.
            this.routeService.subscribeToRouteChangeEnd(ev => {
                if (ev.currentRoute.route === OfficeRoute.Content) {
                    if (expiryTimeout) {
                        this.windowService.nativeWindow.clearTimeout(expiryTimeout);
                        expiryTimeout = undefined;
                    }
                } else {
                    if (!expiryTimeout) {
                        const timeout = Math.max(timeoutInterval - (Date.now() - pageLoadStart), 0);
                        expiryTimeout = this.windowService.nativeWindow.setTimeout(
                            () => {
                                if (this.currentState === AuthState.SignedIn) {
                                    this.updateCurrentState({ LoginState: AuthState.Expired });
                                }
                            },
                            timeout);
                    }
                }
            });
        }
    }

    private async watchAccessTokenPermanentError(): Promise<void> {
        // Kick off timer to trigger ReauthenticationRequired state if permanent error was detected when
        // access tokens were collected
        if (await this.accessTokenService.hasPermanentAuthError()) {
            const intervalInMinutes = this.configService.config.reauthenticationForAccessTokenPermanentErrorIntervalInMinutes;
            if (intervalInMinutes > 0) {
                this.windowService.nativeWindow.setTimeout(
                    () => {
                        if (this.currentState === AuthState.SignedIn) {
                            this.updateCurrentState({ LoginState: AuthState.ReauthenticationRequired });
                        }
                    },
                    intervalInMinutes * 60000);
            }
        }
    }

    private watchPageVisibilityChanges(): void {
        // Handle when page becomes visible and trigger check for auth state
        // IMPORTANT: It is possible that this code runs before page becomes visible on initial page load.
        // It does not make sense to check for login right after page is loaded so start watching for page
        // visibility changes after some time (use checkLogin interval as it seem to be a resonable initial delay).
        this.windowService.nativeWindow.setTimeout(
            () => {
                this.pageVisibilityService.addOnVisibilityChanged(() => {
                    if (this.pageVisibilityService.isVisible()) {
                        this.checkAuthState();
                    }
                });
            },
            this.configService.config.checkAuthStateIntervalInSeconds * 1000);
    }

    private scheduleCheckAuthState(): void {
        if (this.currentState === AuthState.SignedIn && !this.checkAuthStateTimeout && this.configService.config.checkAuthStateIntervalInSeconds > 0) {
            this.checkAuthStateTimeout = this.windowService.nativeWindow.setTimeout(
                () => this.checkAuthState(),
                this.configService.config.checkAuthStateIntervalInSeconds * 1000);
        }
    }

    private checkAuthState(): void {
        if (this.checkAuthStateTimeout) {
            this.windowService.nativeWindow.clearTimeout(this.checkAuthStateTimeout);
            this.checkAuthStateTimeout = null;
        }

        this.queryParamProcessor
            .then(() => {
                if (this.currentState === AuthState.SignedIn && this.pageVisibilityService.isVisible()) {
                    return this.instrumentedHttp
                        .get<CheckLoginResponse>(KnownInstrumentationEvents.CheckLoginRequest, KnownRequestInstrumentationEvents.CheckLoginRequest, this.checkAuthStateEndpoint, this.checkAuthStateRequestOptions, FetchStrategy.NetworkOnly)
                        .then(({ data }) => {
                            if (!!data && this.currentState === AuthState.SignedIn) {
                                this.updateCurrentState(data);
                            }
                        });
                }
            })
            .catch(error => {
                this.globalErrorHandler.handleError(error, ErrorId.QGMJO);
            })
            .then(() => this.scheduleCheckAuthState());
    }

    private updateCurrentState(response: CheckLoginResponse) {
        const state: AuthState = response.LoginState;
        if (this.currentState === state) {
            return;
        }

        this.currentState = state;

        const params = {};
        params[InstrumentationParam.State] = this.currentState;
        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.AuthStateChanged, params);
        logFeature({ featureName: KnownFeatureNames.Auth, eventName: KnownFeatureInstrumentationEvents.AuthStateChanged, params});

        this.eventService.broadcastEvent({
            name: AngularEvents.authStateChanged,
            args: [response]
        });
    }

    private setupActivityTimeoutSignoutListener() {
        this.windowService.nativeWindow.addEventListener('message', event => {
            if (event && event.data) {
                let data: AbtSignoutEventData;
                try {
                    data = JSON.parse(event.data) as AbtSignoutEventData;
                } catch {
                    // Messages from other sources may not be JSON, so swallow all errors
                }

                if (data && data.eventId === 'abtSignOutEventForHost') {
                    this.updateCurrentState({ LoginState: AuthState.SignedOutBasedOnActivityAuto });
                }

                if (data && data.eventId === 'abtSignOutEventForHostUserGenerated') {
                    this.updateCurrentState({ LoginState: AuthState.SignedOutBasedOnActivityUserClick });
                }
            }
        });
    }
}
